import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import { ScoreGauge } from "@/components/ScoreGauge";

const NAV: Array<{ to: string; label: string; glyph: string }> = [
  { to: "/live", label: "Live Scan", glyph: "◉" },
  { to: "/findings", label: "Findings", glyph: "◆" },
  { to: "/devices", label: "Devices", glyph: "▤" },
  { to: "/reports", label: "Reports", glyph: "▦" },
  { to: "/notifications", label: "Inbox", glyph: "✉" },
  { to: "/halt", label: "Halt", glyph: "✕" },
  { to: "/account", label: "Account", glyph: "◐" },
];

function Clock(): JSX.Element {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <span className="code text-xs text-chrome-300">
      {now.toISOString().slice(11, 19)} UTC
    </span>
  );
}

export function Layout(): JSX.Element {
  const { data: household } = useQuery({
    queryKey: ["account"],
    queryFn: api.account,
  });
  const { data: live } = useQuery({
    queryKey: ["scans", "live"],
    queryFn: api.liveScan,
    refetchInterval: 5000,
  });
  const { data: notifs } = useQuery({
    queryKey: ["notifications"],
    queryFn: api.notifications,
    refetchInterval: 10000,
  });
  const unreadCount = notifs?.notifications.length ?? 0;

  return (
    <div className="min-h-screen text-chrome-100">
      <div className="mx-auto flex min-h-screen max-w-[1400px]">
        <aside className="sticky top-0 flex h-screen w-64 shrink-0 flex-col border-r border-ink-400 bg-ink-800/70 px-5 py-6 backdrop-blur-md">
          <div className="flex items-center gap-2">
            <div className="grid size-8 place-items-center rounded-md border border-cyber-400/40 bg-ink-700 text-cyber-400 shadow-glow-cyber">
              <span className="font-semibold">RT</span>
            </div>
            <div className="text-sm font-semibold tracking-[0.15em] text-chrome-100">
              HOME<span className="text-cyber-400 text-glow-cyber">.RT</span>
            </div>
          </div>
          <div className="mt-1 ml-10 label-overline">
            household control plane
          </div>

          <nav className="mt-8 flex flex-col gap-0.5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  [
                    "group relative flex items-center gap-3 rounded px-3 py-2 text-sm transition",
                    isActive
                      ? "bg-cyber-400/10 text-chrome-50"
                      : "text-chrome-300 hover:bg-ink-600 hover:text-chrome-100",
                  ].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute inset-y-1 left-0 w-0.5 rounded-full bg-cyber-400 shadow-[0_0_10px_#22d3ee]" />
                    )}
                    <span
                      className={[
                        "text-sm",
                        isActive ? "text-cyber-400" : "text-chrome-400",
                      ].join(" ")}
                    >
                      {item.glyph}
                    </span>
                    <span className="font-medium tracking-wide">{item.label}</span>
                    {item.to === "/live" && live?.status === "running" && (
                      <span className="ml-auto flex items-center gap-1 text-[10px] uppercase tracking-widest text-crit">
                        <span className="pulse-dot text-crit" />
                        live
                      </span>
                    )}
                    {item.to === "/notifications" && unreadCount > 0 && (
                      <span className="ml-auto rounded-full bg-crit/20 px-2 py-0.5 text-[10px] font-semibold text-crit-400 tabular-nums">
                        {unreadCount}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="mt-auto" />

          {household && (
            <div className="panel-raised mt-6 p-4">
              <div className="label-overline">Posture score</div>
              <div className="mt-2 flex items-center gap-3">
                <ScoreGauge value={household.current_score.value} />
                <div>
                  <div className="text-cyber-400 text-glow-cyber font-mono text-3xl font-semibold">
                    {household.current_score.letter}
                  </div>
                  <div className="text-xs text-chrome-300 tabular-nums">
                    {household.current_score.value}
                    <span className="text-chrome-500">/100</span>
                  </div>
                </div>
              </div>
              {typeof household.current_score.delta_30d === "number" && (
                <div className="mt-3 flex items-center gap-2 text-[11px]">
                  <span
                    className={
                      household.current_score.delta_30d >= 0
                        ? "text-ok-400"
                        : "text-crit-400"
                    }
                  >
                    {household.current_score.delta_30d >= 0 ? "▲" : "▼"}{" "}
                    {Math.abs(household.current_score.delta_30d)}
                  </span>
                  <span className="text-chrome-400">over 30d</span>
                </div>
              )}
            </div>
          )}

          <div className="mt-4 flex items-center justify-between border-t border-ink-400 pt-4 text-[11px] text-chrome-400">
            <span className="flex items-center gap-1.5">
              <span className="pulse-dot text-ok" />
              <span>link ok</span>
            </span>
            <Clock />
          </div>
        </aside>

        <main className="flex-1 px-10 py-8 animate-flicker_in">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
